import { ApVendor } from 'src/app/classes/models/ap-vendor';
import { VendorTrade } from 'src/app/classes/models/vendor-trade';
import { VendorTradeState } from 'src/app/classes/models/vendor-trade-state';
import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { VendorRanking } from 'src/app/interfaces/models/vendor-ranking';

import { Address } from './address';
import { BaseModel } from './base-model';
import { ChangeHistory } from './change-history';
import { Company } from './company';
import { CompanyVendor } from './company-vendor';
import { ComplianceFinding } from './compliance-finding';
import { Contact } from './contact';
import { Site } from './site';
import { Vendor } from './vendor';
import { VendorReview } from './vendor-review';

export class ClientRegisteredVendor extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ClientRegisteredVendorProperties: (
    | {
        property: keyof ClientRegisteredVendor;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof ClientRegisteredVendor;
        type: 'alias';
      }
    | {
        property: keyof ClientRegisteredVendor;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'legalName', type: 'column', dataType: 'string' },
    { property: 'dba', type: 'column', dataType: 'string' },
    { property: 'ein', type: 'column', dataType: 'string' },
    { property: 'ssn', type: 'column', dataType: 'string' },
    { property: 'bsn', type: 'column', dataType: 'string' },
    { property: 'rsn', type: 'column', dataType: 'string' },
    { property: 'website', type: 'column', dataType: 'string' },
    { property: 'companyType', type: 'column', dataType: 'string' },
    { property: 'isSoleProprietorship', type: 'column', dataType: 'boolean' },
    { property: 'taxIdentification', type: 'column', dataType: 'string' },
    { property: 'jurisdiction', type: 'column', dataType: 'string' },
    { property: 'llcType', type: 'column', dataType: 'string' },
    { property: 'address1', type: 'column', dataType: 'string' },
    { property: 'address2', type: 'column', dataType: 'string' },
    { property: 'city', type: 'column', dataType: 'string' },
    { property: 'state', type: 'column', dataType: 'string' },
    { property: 'zip', type: 'column', dataType: 'string' },
    { property: 'country', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'phone2', type: 'column', dataType: 'string' },
    { property: 'fax', type: 'column', dataType: 'string' },
    { property: 'mailingAddress1', type: 'column', dataType: 'string' },
    { property: 'mailingAddress2', type: 'column', dataType: 'string' },
    { property: 'mailingCity', type: 'column', dataType: 'string' },
    { property: 'mailingState', type: 'column', dataType: 'string' },
    { property: 'mailingZip', type: 'column', dataType: 'string' },
    { property: 'isOnSite', type: 'column', dataType: 'boolean' },
    { property: 'isProfessional', type: 'column', dataType: 'boolean' },
    { property: 'isSupplier', type: 'column', dataType: 'boolean' },
    { property: 'isClaimed', type: 'column', dataType: 'boolean' },
    { property: 'claimCode', type: 'column', dataType: 'string' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },
    { property: 'issue1099', type: 'column', dataType: 'boolean' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'latitude', type: 'column', dataType: 'string' },
    { property: 'longitude', type: 'column', dataType: 'string' },
    { property: 'requesterFirstName', type: 'column', dataType: 'string' },
    { property: 'requesterLastName', type: 'column', dataType: 'string' },
    {
      property: 'preventRenewalInactivate',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'stateToVerify', type: 'column', dataType: 'string' },
    { property: 'isTakeOver', type: 'column', dataType: 'boolean' },
    { property: 'isOffsite', type: 'column', dataType: 'boolean' },

    { property: 'lastRanBusinessVerificationReportDate', type: 'alias' },
    { property: 'hasBusinessReportData', type: 'alias' },

    { property: 'Company', model: 'Company', type: 'association' },
    { property: 'CompanyVendors', model: 'CompanyVendor', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    {
      property: 'ChangeHistories',
      model: 'ChangeHistory',
      type: 'association'
    },
    { property: 'Addresses', model: 'Address', type: 'association' },
    { property: 'StreetAddress', model: 'StreetAddress', type: 'association' },
    {
      property: 'MailingAddress',
      model: 'MailingAddress',
      type: 'association'
    },
    {
      property: 'RemitToAddress',
      model: 'RemitToAddress',
      type: 'association'
    },
    { property: 'Contacts', model: 'Contact', type: 'association' },
    { property: 'VendorTrades', model: 'VendorTrade', type: 'association' },
    { property: 'VendorRankings', model: 'VendorRanking', type: 'association' },
    { property: 'VendorReviews', model: 'VendorReview', type: 'association' },
    {
      property: 'VendorTradeStates',
      model: 'VendorTradeState',
      type: 'association'
    },
    { property: 'Sites', model: 'Site', type: 'association' },
    { property: 'ApVendors', model: 'ApVendor', type: 'association' }
  ];

  static {
    ClientRegisteredVendor.ClientRegisteredVendorProperties.forEach(
      (propertyObject) => {
        const { property, type } = propertyObject;
        if (type === 'column') {
          ClientRegisteredVendor.columnMap.set(property, propertyObject);
        }
        if (type === 'alias') {
          ClientRegisteredVendor.aliasedColumnMap.set(
            property,
            propertyObject as { property: string; type: 'alias' }
          );
        }
        if (type === 'association') {
          ClientRegisteredVendor.associationMap.set(
            property,
            propertyObject as {
              property: string;
              type: 'association';
              model: string;
            }
          );
        }
      }
    );
  }

  constructor(ClientRegisteredVendorObject: Partial<ClientRegisteredVendor>) {
    super();

    ClientRegisteredVendor.ClientRegisteredVendorProperties.forEach(
      (propertyObject) => {
        const { property } = propertyObject;
        const value = ClientRegisteredVendorObject[property];
        if (value !== undefined) {
          Object.assign(this, { [property]: value });
        }
      }
    );
  }

  id?: number;
  legalName?: string;
  dba?: string | null;
  ein?: string | null;
  ssn?: string | null;
  bsn?: string | null;
  rsn?: string | null;
  website?: string | null;
  companyType?:
    | 'C Corporation'
    | 'S Corporation'
    | 'Limited Liability Company (LLC)'
    | 'Partnership'
    | 'Sole Proprietorship'
    | 'Corporation'
    | null;
  isSoleProprietorship?: boolean;
  taxIdentification?: 'EIN' | 'SSN' | null;
  jurisdiction?:
    | 'AB'
    | 'BC'
    | 'MB'
    | 'NB'
    | 'NL'
    | 'NT'
    | 'NS'
    | 'NU'
    | 'ON'
    | 'PE'
    | 'QC'
    | 'SK'
    | 'YT'
    | 'Federal'
    | 'Both'
    | null;
  llcType?: 'C' | 'S' | 'P' | 'disregardedEntity' | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: 'US' | 'CA';
  phone?: string | null;
  phone2?: string | null;
  fax?: string | null;
  mailingAddress1?: string | null;
  mailingAddress2?: string | null;
  mailingCity?: string | null;
  mailingState?: string | null;
  mailingZip?: string | null;
  isOnSite?: boolean;
  isProfessional?: boolean;
  isSupplier?: boolean;
  isClaimed?: boolean;
  claimCode?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
  isArchived?: boolean;
  issue1099?: boolean;
  VendorId?: number | null;
  CompanyId?: number;
  accountingId?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  requesterFirstName?: string | null;
  requesterLastName?: string | null;
  preventRenewalInactivate?: boolean;
  stateToVerify?:
    | (
        | 'AL'
        | 'AK'
        | 'AR'
        | 'AZ'
        | 'CA'
        | 'CO'
        | 'CT'
        | 'DC'
        | 'DE'
        | 'FL'
        | 'GA'
        | 'HI'
        | 'IA'
        | 'ID'
        | 'IL'
        | 'IN'
        | 'KS'
        | 'KY'
        | 'LA'
        | 'MA'
        | 'MD'
        | 'ME'
        | 'MI'
        | 'MN'
        | 'MO'
        | 'MS'
        | 'MT'
        | 'NC'
        | 'ND'
        | 'NE'
        | 'NH'
        | 'NJ'
        | 'NM'
        | 'NV'
        | 'NY'
        | 'OH'
        | 'OK'
        | 'OR'
        | 'PA'
        | 'RI'
        | 'SC'
        | 'SD'
        | 'TN'
        | 'TX'
        | 'UT'
        | 'VA'
        | 'VT'
        | 'WA'
        | 'WI'
        | 'WV'
        | 'WY'
        | 'AB'
        | 'BC'
        | 'MB'
        | 'NB'
        | 'NL'
        | 'NS'
        | 'NU'
        | 'ON'
        | 'PE'
        | 'QC'
        | 'SK'
        | 'NT'
        | 'YT'
      )
    | null;
  isTakeOver?: boolean;
  isOffsite?: boolean;

  lastRanBusinessVerificationReportDate?: Date;
  hasBusinessReportData?: boolean;

  Company?: Company;
  CompanyVendors?: CompanyVendor[];
  Vendor?: Vendor;
  ChangeHistories?: ChangeHistory[];
  Addresses?: Address[];
  StreetAddress?: Address;
  MailingAddress?: Address;
  RemitToAddress?: Address;
  Contacts?: Contact[];
  VendorTrades?: VendorTrade[];
  VendorRankings?: VendorRanking[];
  VendorTradeStates?: VendorTradeState[];
  Sites?: Site[];
  VendorReviews?: VendorReview[];
  ComplianceFinding?: ComplianceFinding;
  ApVendors?: ApVendor[];
}
